import React, { useState, useEffect } from 'react';
import { Crown, Check, X } from 'lucide-react';

export default function PremiumUpgrade({ userEmail, onClose }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Check if returning from Stripe with session_id
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    
    if (sessionId) {
      console.log('Detected return from Stripe, checking payment status...');
      pollPaymentStatus(sessionId);
    }
  }, []);

  // Poll payment status after redirect from Stripe
  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000; // 2 seconds

    if (attempts >= maxAttempts) {
      setError('Payment verification timed out. Please contact support if you were charged.');
      setCheckingPayment(false);
      return;
    }

    setCheckingPayment(true);
    setPaymentStatus('Verifying payment...');

    try {
      const response = await fetch(`${API_URL}/api/payments/status/${sessionId}`, {
        headers: {
          'X-User-Email': userEmail || 'guest@example.com'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to verify payment');
      }

      const data = await response.json();
      
      if (data.payment_status === 'paid' && data.premium_granted) {
        setPaymentStatus('Payment successful! 🎉 Premium access granted.');
        setCheckingPayment(false);
        
        // Clear URL params
        window.history.replaceState({}, document.title, window.location.pathname);
        
        // Reload to show premium features
        setTimeout(() => {
          window.location.reload();
        }, 2000);
        return;
      } else if (data.status === 'expired') {
        setError('Payment session expired. Please try again.');
        setCheckingPayment(false);
        return;
      }

      // Continue polling if still pending
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (err) {
      console.error('Error checking payment status:', err);
      setError('Failed to verify payment. Please refresh the page.');
      setCheckingPayment(false);
    }
  };

  const handleUpgrade = async () => {
    setLoading(true);
    setError('');

    try {
      // Get current origin for success/cancel URLs
      const originUrl = window.location.origin;

      const response = await fetch(`${API_URL}/api/payments/create-checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Email': userEmail || 'guest@example.com'
        },
        body: JSON.stringify({
          origin_url: originUrl
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create checkout session');
      }

      const data = await response.json();
      
      // Redirect to Stripe Checkout
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (err) {
      console.error('Payment error:', err);
      setError(err.message || 'Failed to start checkout. Please try again.');
      setLoading(false);
    }
  };

  if (checkingPayment) {
    return (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bunker-panel max-w-md w-full p-8 text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-green-500 border-t-transparent mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-green-400 mb-2">Verifying Payment</h2>
          <p className="text-gray-300">{paymentStatus}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bunker-panel max-w-2xl w-full p-8 relative">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
        >
          <X className="w-6 h-6" />
        </button>

        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-full mb-4">
            <Crown className="w-8 h-8 text-black" />
          </div>
          <h1 className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-yellow-600 mb-2">
            Upgrade to Premium
          </h1>
          <p className="text-gray-300">Unlock the full Global Thermo III experience</p>
        </div>

        {/* Features */}
        <div className="grid md:grid-cols-2 gap-4 mb-8">
          <div className="bunker-panel p-4 border border-green-500/30">
            <div className="flex items-start gap-3">
              <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-bold text-green-400 mb-1">Unlimited Games</h3>
                <p className="text-sm text-gray-300">Play as many campaigns as you want, no restrictions</p>
              </div>
            </div>
          </div>

          <div className="bunker-panel p-4 border border-green-500/30">
            <div className="flex items-start gap-3">
              <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-bold text-green-400 mb-1">Ad-Free Experience</h3>
                <p className="text-sm text-gray-300">No interruptions, pure strategy gameplay</p>
              </div>
            </div>
          </div>

          <div className="bunker-panel p-4 border border-green-500/30">
            <div className="flex items-start gap-3">
              <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-bold text-green-400 mb-1">Priority Support</h3>
                <p className="text-sm text-gray-300">Get help faster when you need it</p>
              </div>
            </div>
          </div>

          <div className="bunker-panel p-4 border border-green-500/30">
            <div className="flex items-start gap-3">
              <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-bold text-green-400 mb-1">Future Updates</h3>
                <p className="text-sm text-gray-300">Access to all upcoming premium features</p>
              </div>
            </div>
          </div>
        </div>

        {/* Price */}
        <div className="text-center mb-6">
          <div className="inline-block bunker-panel border border-yellow-500/50 p-6">
            <div className="text-sm text-gray-400 mb-1">One-Time Payment</div>
            <div className="text-5xl font-bold text-yellow-400 mb-1">$2.99</div>
            <div className="text-sm text-gray-400">Lifetime Premium Access</div>
          </div>
        </div>

        {/* Error message */}
        {error && (
          <div className="bg-red-500/20 border border-red-500 text-red-400 p-4 rounded mb-6 text-center">
            {error}
          </div>
        )}

        {/* Upgrade button */}
        <button
          onClick={handleUpgrade}
          disabled={loading}
          className="w-full btn-primary py-4 text-lg font-bold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
              Processing...
            </>
          ) : (
            <>
              <Crown className="w-5 h-5" />
              Upgrade to Premium
            </>
          )}
        </button>

        {/* Security notice */}
        <div className="text-center text-xs text-gray-500 mt-4">
          🔒 Secure payment powered by Stripe • No subscription, pay once
        </div>
      </div>
    </div>
  );
}
